/**
 * Article Extractor (shared)
 *
 * The DOM-to-article heuristics, used from two places: the backend (where the
 * Document comes from JSDOM) and the panel frontend (where it comes from the
 * browser's own DOMParser, so that articles whose publisher blocks our server
 * can still be read from the user's residential IP).
 *
 * This module deliberately imports nothing. `backend/` and `frontend/` are
 * separate packages with separate node_modules, and a bare `@mozilla/readability`
 * import here would resolve against whichever tree happens to sit above this
 * file. So Readability and the HTML parser are passed in by the caller instead.
 */

/**
 * Shortest article we treat as real. Below this the page is almost always a
 * JS shell, a bot challenge or a cookie wall, and summarising it produces a
 * confident-sounding article built out of navigation text - worse than an
 * outright failure, because nothing flags it for review.
 */
export const minArticleChars = 250;

/**
 * How much longer the JSON-LD body must be than Readability's before it takes
 * over a body that already cleared minArticleChars. Readability scores some
 * templates as boilerplate so confidently that it returns the footer's
 * about-the-channel blurb instead of the story (gujaratfirst.com yields 681
 * characters of channel history for a 3,868-character article), and that
 * passes every length check while being entirely the wrong text. Requiring a
 * clear margin leaves publishers who put only a teaser in their JSON-LD on
 * Readability's body.
 */
const jsonLdPreferenceRatio = 1.5;

/**
 * How much larger (by area) a body image must be before it is preferred
 * over the og:image/twitter:image rendition
 */
const bodyImagePreferenceRatio = 1.05;

/**
 * Filenames that are never the main article image. Matched against the last
 * path segment only, because publishers do store real photos in directories
 * named after these words (Hindustan Times serves article images from
 * /ht-img/img/<date>/1600x900/logo/<filename>)
 */
const junkImageNamePattern = /(logo|icon|favicon|avatar|author|sprite|placeholder|advert|1x1|blank|spacer)/i;

/**
 * Ad-serving directories, matched anywhere in the path
 */
const adPathPattern = /\/(ads?|adserver|advert[a-z]*)\//i;

/**
 * Resolve an image URL against the document it came from.
 *
 * Readability absolutises the src values inside the body it extracts, but a
 * meta tag read through getAttribute hands back whatever the publisher wrote -
 * and a fair few write `/img/2026/photo.jpg`. A relative URL is worse than no
 * URL downstream: the uploader cannot fetch it, so the short silently falls
 * back to a category stock image with nothing saying why.
 *
 * Both callers give the document a real base (JSDOM's `url` option on the
 * backend, an injected <base> in the browser), so baseURI is the article's own
 * address. If it somehow is not, the raw value is returned unchanged rather
 * than letting a throw take the whole extraction down.
 */
const absolutiseUrl = (value, doc) => {
  if (!value) return value;

  try {
    return new URL(value, doc.baseURI).href;
  } catch {
    return value;
  }
};

/**
 * Decide whether an image src is boilerplate rather than article content
 */
const isJunkImage = (src) => {
  if (src.startsWith('data:') || src.startsWith('blob:')) return true;
  if (adPathPattern.test(src)) return true;

  const filename = src.split('?')[0].split('#')[0].split('/').pop() || '';
  return junkImageNamePattern.test(filename);
};

/**
 * Read an image's rendered dimensions from its attributes
 * Publishers expose these as width/height and/or data-width/data-height
 */
const getImageDimensions = (img) => ({
  width: parseInt(img.getAttribute('width') || img.getAttribute('data-width')) || 0,
  height: parseInt(img.getAttribute('height') || img.getAttribute('data-height')) || 0,
});

/**
 * Find the largest usable image inside Readability's extracted article body
 * Returns { url, width, height } or null. Readability absolutises image src
 * values, so the returned URL is already fully qualified.
 */
const findLargestBodyImage = (articleContent, parseHtml) => {
  const images = parseHtml(articleContent).querySelectorAll('img');

  let largest = null;
  let firstPlausible = null;

  for (const img of images) {
    const src = img.getAttribute('src');
    if (!src || isJunkImage(src)) {
      continue;
    }

    const { width, height } = getImageDimensions(img);

    // Keep the first reasonably-sized image so we always have a candidate,
    // even on sites that ship no width/height attributes
    if (!firstPlausible &&
        (width >= 300 || height >= 200 || (!width && !height && src.match(/\.(jpg|jpeg|png|webp)(\?|$)/i)))) {
      firstPlausible = { url: src, width, height };
    }

    // Only images with both dimensions known can be compared by area
    if (width >= 300 && height >= 200 &&
        (!largest || width * height > largest.width * largest.height)) {
      largest = { url: src, width, height };
    }
  }

  return largest || firstPlausible;
};

/**
 * Pull the article body out of JSON-LD
 * Publishers emit this for search engines even when their markup defeats
 * Readability, so it is the most reliable second opinion available.
 */
const extractFromJsonLd = (doc) => {
  for (const node of doc.querySelectorAll('script[type="application/ld+json"]')) {
    let parsed;
    try {
      parsed = JSON.parse(node.textContent);
    } catch {
      continue;
    }

    // Publishers nest the NewsArticle at the top level, in an array, or under
    // @graph, so walk the whole structure rather than guessing the shape
    const stack = Array.isArray(parsed) ? [...parsed] : [parsed];
    while (stack.length) {
      const entry = stack.pop();
      if (!entry || typeof entry !== 'object') continue;

      if (typeof entry.articleBody === 'string' && entry.articleBody.trim().length >= minArticleChars) {
        return {
          title: typeof entry.headline === 'string' ? entry.headline : '',
          content: entry.articleBody.trim(),
        };
      }

      for (const value of Object.values(entry)) {
        if (value && typeof value === 'object') stack.push(value);
      }
    }
  }

  return null;
};

/**
 * Last-resort extraction: the container whose paragraph text is densest
 * Readability scores some templates as boilerplate and returns almost nothing
 * (Anadolu renders the body in deeply nested utility-class divs, yielding 57
 * characters out of a 1,900-character article). Picking the block with the
 * most paragraph text recovers those without per-site selectors.
 */
const extractDensestTextBlock = (doc) => {
  let best = '';

  for (const element of doc.querySelectorAll('article, main, section, div')) {
    const text = [...element.querySelectorAll('p')]
      .map((p) => p.textContent.trim())
      // Short paragraphs are captions, bylines and share prompts
      .filter((t) => t.length > 40)
      .join('\n\n');

    if (text.length > best.length) {
      best = text;
    }
  }

  return best.length >= minArticleChars ? { title: '', content: best } : null;
};

/**
 * Turn a parsed page into an article.
 *
 * Returns `{ title, content, excerpt, imageUrl }`. `content` can come back
 * shorter than minArticleChars - deciding what to do about that belongs to the
 * caller, because the two callers report it differently.
 *
 * @param {Document} doc - the page, already parsed. Not mutated.
 * @param {object} env
 * @param {Function} env.Readability - the @mozilla/readability constructor
 * @param {(html: string) => Document|DocumentFragment} env.parseHtml - parse an
 *   HTML fragment into something queryable, for reading Readability's own output
 */
export const extractArticleFromDocument = (doc, { Readability, parseHtml }) => {
  // Readability mutates the document it parses, so hand it a clone and keep
  // the original intact for the fallback extractors and image lookup below
  const article = new Readability(doc.cloneNode(true)).parse();

  let title = article?.title || '';
  let content = (article?.textContent || '').trim();
  let excerpt = article?.excerpt || '';

  // Readability handles the vast majority of pages, but the publisher's own
  // JSON-LD wins whenever it carries more of the story. A thin body loses to
  // anything longer; a body that already reads as an article has to be beaten
  // by a clear margin, since returning confident boilerplate at a plausible
  // length is the other way Readability fails
  const jsonLd = extractFromJsonLd(doc);
  const lengthToBeat =
    content.length < minArticleChars ? content.length : content.length * jsonLdPreferenceRatio;

  if (jsonLd && jsonLd.content.length > lengthToBeat) {
    content = jsonLd.content;
    title = title || jsonLd.title;
    excerpt = '';
  }

  // Scanning every container for paragraph density is the last resort, and
  // only earns its cost once both of the above have come back thin
  if (content.length < minArticleChars) {
    const fallback = extractDensestTextBlock(doc);

    if (fallback && fallback.content.length > content.length) {
      content = fallback.content;
      title = title || fallback.title;
      excerpt = '';
    }
  }

  if (!title) {
    title =
      doc.querySelector('meta[property="og:title"]')?.getAttribute('content') ||
      doc.querySelector('title')?.textContent?.trim() ||
      '';
  }

  // Extract image URL. Meta tags are the usual primary source, but several
  // publishers put a cropped social-share rendition there while the article
  // body carries the uncropped original (eenadu serves
  // featureimages/680X310/<id>-680X310.webp as og:image but
  // article_multiple_images/<id>_<slug>.webp at 650x362 in the body).
  // So prefer the body image whenever it is measurably larger.
  const metaImage =
    doc.querySelector('meta[property="og:image"]')?.getAttribute('content') ||
    doc.querySelector('meta[property="og:image:secure_url"]')?.getAttribute('content') ||
    doc.querySelector('meta[name="twitter:image"]')?.getAttribute('content') ||
    null;

  const metaWidth = parseInt(doc.querySelector('meta[property="og:image:width"]')?.getAttribute('content')) || 0;
  const metaHeight = parseInt(doc.querySelector('meta[property="og:image:height"]')?.getAttribute('content')) || 0;

  const bodyImage = article?.content ? findLargestBodyImage(article.content, parseHtml) : null;

  let imageUrl = metaImage;

  if (bodyImage) {
    const metaArea = metaWidth * metaHeight;
    const bodyArea = bodyImage.width * bodyImage.height;

    // Only override when both sides declared their size, otherwise the
    // comparison is meaningless and the meta image stays the safer pick
    const bodyIsLarger = metaArea > 0 && bodyArea > metaArea * bodyImagePreferenceRatio;

    if (!imageUrl || bodyIsLarger) {
      imageUrl = bodyImage.url;
    }
  }

  return {
    title,
    content,
    excerpt: excerpt || content.slice(0, 200),
    // Body images arrive absolute from Readability; a meta image does not
    imageUrl: absolutiseUrl(imageUrl, doc),
  };
};
